import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, FilePlus2, Loader2, Pencil, Send, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import CguContent from "@/components/price/CguContent";
import CguAcceptancesPanel from "@/components/admin/CguAcceptancesPanel";
import CguPublishDialog from "@/components/admin/CguPublishDialog";

const STATUS = { draft: ["Brouillon", "bg-amber-100 text-amber-900"], published: ["En vigueur", "bg-emerald-100 text-emerald-900"], archived: ["Archivée", "bg-slate-200 text-slate-700"] };

// Admin : versions des CGU, édition des brouillons, publication et acceptations.
export default function CguAdminTab() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [publish, setPublish] = useState(null);
  const load = useCallback(() => api.get("/admin/cgu/versions").then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e)); } };
  if (!data) return <Loader2 className="h-6 w-6 animate-spin" />;

  return (
    <div className="space-y-5" data-testid="cgu-admin-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-[Outfit] text-2xl font-black">Conditions Générales d&apos;Utilisation</h2>
        <button type="button" onClick={() => setEdit({ version: "", title: "Conditions Générales d'Utilisation — Price Covoit", summary: "", content: "" })}
          className="street-press inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-[13px] font-black text-primary-foreground street-border-thin" data-testid="cgu-new-draft">
          <FilePlus2 className="h-4 w-4" />Nouveau brouillon
        </button>
      </div>
      <p className="text-[12px] font-semibold text-foreground/60">Rien n&apos;est publié automatiquement. Publier une version archive la précédente ; les anciennes versions restent consultables et peuvent être dupliquées pour être restaurées.</p>

      <div className="space-y-2">
        {data.items.map((v) => (
          <div key={v.id} className="rounded-2xl bg-white p-3 street-border-thin" data-testid={`cgu-version-${v.version}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 font-[Outfit] text-[16px] font-black">
                  Version {v.version}
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-black ${STATUS[v.status][1]}`} data-testid={`cgu-version-status-${v.version}`}>{STATUS[v.status][0]}</span>
                </div>
                <div className="text-[12px] font-semibold text-foreground/60">
                  {v.published_at ? `Publiée le ${new Date(v.published_at).toLocaleString("fr-FR")}` : `Créée le ${new Date(v.created_at).toLocaleString("fr-FR")}`}
                  {v.status !== "draft" && ` · ${v.acceptances} acceptation(s)`}
                  {v.status === "draft" && v.placeholders > 0 && <span className="font-black text-red-600"> · {v.placeholders} passage(s) [entre crochets] à compléter</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <IconBtn Icon={Eye} label="Voir" onClick={() => setPreview(v)} testid={`cgu-preview-${v.version}`} />
                {v.status === "draft" && <IconBtn Icon={Pencil} label="Modifier" onClick={() => setEdit(v)} testid={`cgu-edit-${v.version}`} />}
                <IconBtn Icon={Copy} label={v.status === "draft" ? "Dupliquer" : "Restaurer en brouillon"} onClick={() => act(() => api.post(`/admin/cgu/versions/${v.id}/duplicate`), "Copie créée en brouillon")} testid={`cgu-duplicate-${v.version}`} />
                {v.status === "draft" && <IconBtn Icon={Send} label="Publier" onClick={() => setPublish(v)} testid={`cgu-publish-${v.version}`} />}
                {v.status === "draft" && <IconBtn Icon={Trash2} label="Supprimer" onClick={() => window.confirm("Supprimer ce brouillon ?") && act(() => api.delete(`/admin/cgu/versions/${v.id}`), "Brouillon supprimé")} testid={`cgu-delete-${v.version}`} />}
              </div>
            </div>
          </div>
        ))}
      </div>

      <CguAcceptancesPanel versions={data.items.filter((v) => v.status !== "draft")} />

      {edit && <DraftEditor draft={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {preview && <Preview v={preview} onClose={() => setPreview(null)} />}
      {publish && <CguPublishDialog v={publish} onClose={() => setPublish(null)} onDone={() => { setPublish(null); load(); }} />}
    </div>
  );
}

function IconBtn({ Icon, label, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} className="street-press inline-flex items-center gap-1 rounded-full bg-[#FFF8F1] px-3 py-1.5 text-[12px] font-black street-border-thin" data-testid={testid}>
      <Icon className="h-3.5 w-3.5" />{label}
    </button>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4" data-testid={testid} onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-3xl bg-[#FFF8F1] p-5 street-border" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function Preview({ v, onClose }) {
  return (
    <Modal onClose={onClose} testid="cgu-preview-dialog">
      <div className="mb-2 flex items-center justify-between font-[Outfit] text-[18px] font-black">Version {v.version}
        <button type="button" onClick={onClose} className="text-[13px] underline" data-testid="cgu-preview-close">Fermer</button></div>
      <div className="overflow-y-auto"><CguContent content={v.content} testid="cgu-preview-content" /></div>
    </Modal>
  );
}

function DraftEditor({ draft, onClose, onSaved }) {
  const [f, setF] = useState({ version: draft.version, title: draft.title, summary: draft.summary || "", content: draft.content });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("edit");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const area = useRef(null);
  const insert = (before, after = "", placeholder = "") => {
    const el = area.current;
    const a = el ? el.selectionStart : f.content.length;
    const b = el ? el.selectionEnd : f.content.length;
    const sel = f.content.slice(a, b) || placeholder;
    if (!before.startsWith("\n") && a > 0 && !/\s/.test(f.content[a - 1])) before = ` ${before}`;
    setF((x) => ({ ...x, content: x.content.slice(0, a) + before + sel + after + x.content.slice(b) }));
    setTimeout(() => { el?.focus(); el?.setSelectionRange(a + before.length, a + before.length + sel.length); }, 0);
  };
  const TOOLS = [["Article", "\n## ", "", "Article X – Titre"], ["Sous-titre", "\n### ", "", "Sous-titre"], ["Gras", "**", "**", "texte"],
    ["Liste", "\n- ", "", "élément"], ["Lien", "[", "](https://)", "texte du lien"], ["Paragraphe", "\n\n", "", "Nouveau paragraphe"]];
  const save = async () => {
    setBusy(true);
    try {
      if (draft.id) await api.put(`/admin/cgu/versions/${draft.id}`, f); else await api.post("/admin/cgu/versions", f);
      toast.success("Brouillon enregistré"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const input = "w-full rounded-xl bg-white px-3 py-2 text-[14px] font-semibold street-border-thin";
  return (
    <Modal onClose={onClose} testid="cgu-editor-dialog">
      <div className="mb-3 font-[Outfit] text-[18px] font-black">{draft.id ? `Modifier le brouillon ${draft.version}` : "Nouveau brouillon"}</div>
      <div className="grid gap-2 sm:grid-cols-[140px_1fr]">
        <input className={input} placeholder="N° (ex. 2.1)" value={f.version} onChange={set("version")} data-testid="cgu-editor-version" />
        <input className={input} placeholder="Titre" value={f.title} onChange={set("title")} data-testid="cgu-editor-title" />
      </div>
      <input className={`${input} mt-2`} placeholder="Résumé des changements (affiché aux utilisateurs)" value={f.summary} onChange={set("summary")} data-testid="cgu-editor-summary" />
      <div className="mt-3 flex gap-1.5">
        {["edit", "preview"].map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={`rounded-full px-3 py-1 text-[12px] font-black street-border-thin ${tab === t ? "bg-primary text-primary-foreground" : "bg-white"}`} data-testid={`cgu-editor-tab-${t}`}>
            {t === "edit" ? "Texte" : "Aperçu"}
          </button>
        ))}
      </div>
      <div className="mt-2 min-h-0 flex-1 overflow-y-auto">
        {tab === "edit"
          ? (
            <>
              <div className="mb-1.5 flex flex-wrap gap-1" data-testid="cgu-editor-toolbar">
                {TOOLS.map(([label, b, a, ph]) => (
                  <button key={label} type="button" onClick={() => insert(b, a, ph)} className="rounded-lg bg-white px-2.5 py-1 text-[12px] font-black street-border-thin" data-testid={`cgu-editor-tool-${label.toLowerCase()}`}>{label}</button>
                ))}
              </div>
              <textarea ref={area} className={`${input} h-[46vh] font-mono text-[12px]`} value={f.content} onChange={set("content")} data-testid="cgu-editor-content" />
            </>
          )
          : <CguContent content={f.content} testid="cgu-editor-preview" />}
      </div>
      <p className="mt-2 text-[11px] font-semibold text-foreground/60">Format : « ## » pour un article, « - » pour une liste, **texte** pour du gras, [texte](https://adresse) pour un lien.</p>
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-full bg-white px-4 py-2 text-[13px] font-black street-border-thin" data-testid="cgu-editor-cancel">Annuler</button>
        <button type="button" disabled={busy} onClick={save} className="street-press inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-60" data-testid="cgu-editor-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}Enregistrer le brouillon
        </button>
      </div>
    </Modal>
  );
}
